/**
 * UUID生成ユーティリティ
 */

/**
 * UUIDv7生成関数（時間ベースUUID）
 * 
 * UUIDv7は時間順にソート可能なUUIDで、以下の構造を持ちます：
 * - 48bit: タイムスタンプ（ミリ秒）
 * - 12bit: ランダム値
 * - 4bit: バージョン（7）
 * - 2bit: バリアント（10）
 * - 62bit: ランダム値
 * 
 * @returns UUIDv7文字列（例：01234567-89ab-7cde-8f01-234567890abc）
 */
export const generateUUIDv7 = (): string => {
  // 現在時刻のミリ秒タイムスタンプ（48bit）
  const timestamp = Date.now();
  
  // ランダムバイト生成
  const randomBytes = new Uint8Array(10);
  crypto.getRandomValues(randomBytes);
  
  // UUIDv7フォーマット構築
  const timestampHex = timestamp.toString(16).padStart(12, '0');
  
  // バージョン（4bit）とランダム（12bit）
  const version = 7;
  const randomA = ((randomBytes[0] & 0x0f) | (version << 4)).toString(16);
  const randomB = randomBytes[1].toString(16).padStart(2, '0');
  
  // バリアント（2bit）とランダム（62bit）
  const variant = 2; // 10xx pattern
  const randomC = ((randomBytes[2] & 0x3f) | (variant << 6)).toString(16).padStart(2, '0');
  
  // 残りのランダムバイト
  const randomD = Array.from(randomBytes.slice(3, 8))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // UUID形式に組み立て（8-4-4-4-12）
  return [
    timestampHex.slice(0, 8),
    timestampHex.slice(8, 12),
    randomA + randomB,
    randomC + randomBytes[2].toString(16).padStart(2, '0').slice(-2),
    randomD
  ].join('-');
};

/**
 * UUIDv4生成関数（ランダムUUID）
 * 
 * 標準的なランダムUUIDを生成します。
 * 
 * @returns UUIDv4文字列
 */
export const generateUUIDv4 = (): string => {
  // ランダムバイト生成
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  
  // バージョン（4bit）を設定
  randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40; // version 4
  
  // バリアント（2bit）を設定
  randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80; // variant 10
  
  // 16進数文字列に変換
  const hex = Array.from(randomBytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // UUID形式に組み立て（8-4-4-4-12）
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32)
  ].join('-');
};

/**
 * UUID検証関数
 * 
 * @param uuid 検証するUUID文字列
 * @returns 有効なUUIDかどうか
 */
export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-7][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * UUIDバージョン取得関数
 * 
 * @param uuid UUID文字列
 * @returns UUIDのバージョン番号（1-7）、無効な場合はnull
 */
export const getUUIDVersion = (uuid: string): number | null => {
  if (!isValidUUID(uuid)) {
    return null;
  }
  
  const versionChar = uuid.charAt(14); // バージョンは15文字目
  const version = parseInt(versionChar, 16);
  
  return version >= 1 && version <= 7 ? version : null;
};
